// Native EventSource can't attach Authorization headers, so we read the SSE
// stream via fetch + ReadableStream. Only the subset of the spec the backend
// actually emits is implemented (event: <type>\ndata: <json>\n\n + ':' comments).

export interface SseFrame {
  type: string;
  data: unknown;
}

export interface SseHandlers {
  onFrame: (frame: SseFrame) => void;
  onError?: (err: unknown) => void;
  onClose?: () => void;
}

export interface SseOptions {
  url: string;
  token: string;
  signal: AbortSignal;
  handlers: SseHandlers;
}

interface ParsedFrame {
  event: string;
  data: string;
}

function parseFrame(raw: string): ParsedFrame | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of raw.split("\n")) {
    if (!line || line.startsWith(":")) continue; // comment / heartbeat
    const idx = line.indexOf(":");
    if (idx < 0) continue;
    const field = line.slice(0, idx);
    let value = line.slice(idx + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") {
      event = value;
    } else if (field === "data") {
      dataLines.push(value);
    }
  }
  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join("\n") };
}

export async function openSse(opts: SseOptions): Promise<void> {
  const { url, token, signal, handlers } = opts;
  let res: Response;
  try {
    res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "text/event-stream",
      },
      signal,
    });
  } catch (err) {
    if ((err as { name?: string }).name === "AbortError") {
      handlers.onClose?.();
      return;
    }
    handlers.onError?.(err);
    handlers.onClose?.();
    return;
  }
  if (!res.ok || !res.body) {
    handlers.onError?.(new Error(`SSE HTTP ${res.status}`));
    handlers.onClose?.();
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buf = "";
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      // SSE frames separated by blank line (\n\n or \r\n\r\n).
      let sep: number;
      while ((sep = buf.indexOf("\n\n")) >= 0) {
        const raw = buf.slice(0, sep);
        buf = buf.slice(sep + 2);
        const parsed = parseFrame(raw);
        if (!parsed) continue;
        let data: unknown = parsed.data;
        try {
          data = JSON.parse(parsed.data);
        } catch {
          // not JSON — pass through the raw string
        }
        handlers.onFrame({ type: parsed.event, data });
      }
    }
  } catch (err) {
    if ((err as { name?: string }).name !== "AbortError") {
      handlers.onError?.(err);
    }
  } finally {
    handlers.onClose?.();
  }
}
